import { type FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, Navigate, useLocation, useMatch, useParams } from "react-router";
import { LoadingSpinner } from "widgets";
import { toSafeInt } from "widgets/utils";

import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { type BlastHit, type JobResult, JobStatusEnum } from "@/graphql.ts";
import {
  clearAndUnwatchJob,
  getFullJobResult,
  type IJobState,
  loadAndWatchJob,
  markJobAsRead,
} from "@/redux/modules/job.ts";
import { loadView, type SavedViewState } from "@/redux/modules/saved-view.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { SEQUENCE_TYPE } from "@/settings/settings.js";

import BlastResultDetailPage from "../../blast-result/BlastResultDetailPage.js";
import BlastResultOverviewPage from "../../blast-result/BlastResultOverviewPage.js";
import JobSummaryView from "../../blast-result/JobSummaryView.js";
import { ContextWidgetType, type INavigationContextProps } from "./ContextWidget.js";
import ItemPagination from "./ItemPagination.js";
import { type INavPanelCategory } from "./NavigationPanel.js";
import PageLayout from "./PageLayout.js";

const JobDetailPageLayout: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const { jobId } = useParams<"jobId">();

  const { job, isLoading, jobResults } = useSelector<IAppState, IJobState>((state) => state.job);
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const load = useLoadDispatchWithIdAsNumber(loadAndWatchJob, jobId);

  useEffect(() => {
    // load view data only once on start
    if (viewId) {
      dispatch(loadView(viewId));
    }
  }, [dispatch, viewId]);

  useEffect(() => {
    void load()?.then((job) => {
      if (job.status === JobStatusEnum.Completed && !job.notification?.read) {
        dispatch(markJobAsRead(job.id));
      }
    });

    return () => {
      // cleanup
      dispatch(clearAndUnwatchJob());
    };
  }, [dispatch, load]);

  const location = useLocation();
  const queryIdMatch = useMatch("/job/:jobId/results/:queryId/*");
  const queryId = queryIdMatch?.params.queryId;

  const resultsViewMatch = useMatch("/job/:jobId/results/:queryId/:resultsView/*");
  const resultsView = resultsViewMatch?.params.resultsView;

  const hitNumMatch = useMatch("/job/:jobId/results/:queryId/hitdetails/:hitNum/:hitDetailsView/*");
  const hitNum = hitNumMatch?.params.hitNum;
  const hitDetailsView = hitNumMatch?.params.hitDetailsView;

  // index of the query result page
  if (matchPath("/job/:jobId/results/:queryId", location.pathname)) {
    return <Navigate to={`/job/${jobId}/results/${queryId}/summary`} replace />;
  }

  // legacy links, now they are sub pages of /hitdetails
  const legacyHitSummaryMatch = matchPath("/job/:jobId/results/:queryId/hitsummary/:hitNum/*", location.pathname);
  if (legacyHitSummaryMatch) {
    return (
      <Navigate
        to={`/job/${jobId}/results/${queryId}/hitdetails/${legacyHitSummaryMatch?.params.hitNum}/summary`}
        replace
      />
    );
  }
  // legacy links, now they are sub pages of /hitdetails
  const legacyGenomeViewMatch = matchPath("/job/:jobId/results/:queryId/genomeview/:hitNum/*", location.pathname);
  if (legacyGenomeViewMatch) {
    return (
      <Navigate
        to={`/job/${jobId}/results/${queryId}/hitdetails/${legacyGenomeViewMatch?.params.hitNum}/genomeview`}
        replace
      />
    );
  }

  const getNavItems = (): INavPanelCategory[] => {
    const categories: INavPanelCategory[] = [];
    const jobInfoCategory: INavPanelCategory = {
      label: job.eventName || job.name,
      navItems: [
        {
          label: "Job Summary",
          route: "summary",
          pageComponent: <JobSummaryView />,
          default: true,
        },
        {
          label: "Results Overview",
          route: "results",
          disabled: job.status !== JobStatusEnum.Completed,
          exact: true,
          pageComponent: <BlastResultOverviewPage />,
        },
      ],
    };
    categories.push(jobInfoCategory);

    if (queryId) {
      const jobResult: JobResult = getFullJobResult(jobResults, toSafeInt(queryId));
      const queryLabel: string = jobResult
        ? jobResult.query
          ? `${jobResult.query.accession}.${jobResult.query.version}`
          : "[query sequence unavailable]"
        : "";
      const blastQueryCategory: INavPanelCategory = {
        label: queryLabel,
        widget: (
          <ItemPagination
            items={job.results?.map((r) => r?.id) || []}
            selectedItem={toSafeInt(queryId)}
            onPageChangeToRoute={({ currentIndex, items }) =>
              `/job/${jobId}/results/${items[currentIndex]}${!hitNum && resultsView ? "/" + resultsView : ""}`
            }
            hasKeyboard={!hitNum}
            style={{ marginBottom: 5 }}
          />
        ),
        navItems: [
          {
            label: "Graphic Summary",
            route: `results/${queryId}/summary`,
            matchingRoute: "results/:queryId",
            pageComponent: <BlastResultDetailPage />,
          },
          {
            label: "Hits",
            route: `results/${queryId}/hits`,
            matchingRoute: "results/:queryId",
            pageComponent: <BlastResultDetailPage />,
          },
          {
            label: "HSPs",
            route: `results/${queryId}/hsps`,
            matchingRoute: "results/:queryId",
            pageComponent: <BlastResultDetailPage />,
          },
          {
            label: "Alignments",
            route: `results/${queryId}/alignments`,
            matchingRoute: "results/:queryId",
            pageComponent: <BlastResultDetailPage />,
          },
        ],
      };
      categories.push(blastQueryCategory);

      if (hitNum) {
        // the genome view is not always available
        const hit: BlastHit = jobResult?.blastHits[hitNum ? toSafeInt(hitNum) - 1 : 0];
        const pHits =
          (jobResult?.blastHits &&
            jobResult.blastHits.map((h) => (h.target === null || !h.rank ? null : h.rank)).filter((r) => r !== null)) ||
          [];
        const selectedIdx = pHits.findIndex((h) => h === toSafeInt(hitNum));

        const blastHitCategory: INavPanelCategory = {
          label: `Hit ${hitNum}`,
          widget: (
            <ItemPagination
              items={pHits}
              selectedItem={pHits[selectedIdx]}
              onPageChangeToRoute={({ currentIndex, items }) =>
                `/job/${jobId}/results/${queryId}/hitdetails/${items[currentIndex]}/${hitDetailsView}`
              }
              hasKeyboard
              style={{ marginBottom: 5 }}
            />
          ),
          navItems: [
            {
              label: `Summary`,
              route: `results/${queryId}/hitdetails/${hitNum}/summary`,
              matchingRoute: "results/:queryId",
              pageComponent: <BlastResultDetailPage />,
            },
            {
              label: `Genome View`,
              disabled: hit?.target.type.name !== SEQUENCE_TYPE.GENOMIC || hit?.target.organism.id === 0,
              disabledTooltip: "This genome view is only available for genomic BLAST hits",
              route: `results/${queryId}/hitdetails/${hitNum}/genomeview`,
              matchingRoute: "results/:queryId",
              pageComponent: <BlastResultDetailPage />,
            },
          ],
        };
        categories.push(blastHitCategory);
      }
    }

    return categories;
  };

  const getContextProps = (): INavigationContextProps => {
    if (viewId && !isSavedViewLoading && Number.parseInt(view?.id) === viewId) {
      return {
        type: ContextWidgetType.SAVED_VIEW,
        data: view,
      };
    }
  };

  if (!job || (isLoading && Number.parseInt(job.id) !== toSafeInt(jobId))) {
    // jobState.isLoading && job.id === this.props.jobId corresponds to the case where we are reloading the current job because it completed, we do not want to show the loading spinner, we might be on the input page
    return <LoadingSpinner inCenter />;
  }

  return <PageLayout contextWidgetProps={getContextProps()} navPanelCategories={getNavItems()} />;
};

export default JobDetailPageLayout;
